import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/shared/components/ui/dialog";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import { Alert, AlertDescription } from "@/shared/components/ui/alert";
import { BetaBadge } from "@/shared/components/beta-badge";
import { addMatrixRow, createMatrix, matrixQueries } from "@/domains/matrix";
import { extractErrorMessage } from "../lib/extract-error-message";
import { useMatrixTemplates } from "../hooks/use-matrix-templates";
import { ComingSoonBadge } from "./coming-soon-badge";

/**
 * Template payload serialization is covered, but the picker stays disabled
 * until the compatible backend and LangServe execution path are release-ready.
 */
const TEMPLATES_ENABLED = false;

export type CreateMatrixDialogProps = {
  trigger?: React.ReactNode;
  // Controlled mode: the dialog cannot live inside the menu, which unmounts as it closes.
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
};

export function CreateMatrixDialog({
  trigger,
  open: controlledOpen,
  onOpenChange,
}: CreateMatrixDialogProps) {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const open = controlledOpen ?? uncontrolledOpen;
  const setOpen = onOpenChange ?? setUncontrolledOpen;
  const [name, setName] = useState("");
  // "" is the blank matrix; anything else is a template's analysis id.
  const [templateId, setTemplateId] = useState("");
  const [suggestedName, setSuggestedName] = useState("");
  const [error, setError] = useState<string | null>(null);

  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: templates, isLoading: templatesLoading } = useMatrixTemplates(
    open && TEMPLATES_ENABLED,
  );

  const canSubmit = name.trim().length > 0;

  function handlePickTemplate(next: string) {
    const value = next === "blank" ? "" : next;
    setTemplateId(value);
    const picked = templates?.find((template) => template.id === value);
    if (picked && (name.trim() === "" || name === suggestedName)) {
      setName(picked.name);
      setSuggestedName(picked.name);
    }
  }

  const { mutate: submitCreateMatrix, isPending } = useMutation({
    mutationFn: async () => {
      const created = await createMatrix({
        name: name.trim(),
        ...(templateId ? { templateId } : {}),
      });
      // A failure here still leaves a valid matrix, hence the swallowed error.
      await addMatrixRow(created.tenant_collection_id).catch(() => undefined);
      return created;
    },
    onSuccess: ({ tenant_collection_id }) => {
      setOpen(false);
      setName("");
      setTemplateId("");
      setSuggestedName("");
      void queryClient.invalidateQueries(matrixQueries.lists());
      void navigate(`/matrix/${tenant_collection_id}`);
    },
    onError: (mutationError) => {
      setError(
        extractErrorMessage(
          mutationError,
          "Failed to create matrix. Please try again.",
        ),
      );
    },
  });

  function handleCreate() {
    if (!canSubmit) return;
    setError(null);
    submitCreateMatrix();
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        setError(null);
        if (!next) {
          setName("");
          setTemplateId("");
          setSuggestedName("");
        }
      }}
    >
      {trigger ? <DialogTrigger asChild>{trigger}</DialogTrigger> : null}
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create matrix</DialogTitle>
        </DialogHeader>
        <form
          onSubmit={(event) => {
            event.preventDefault();
            handleCreate();
          }}
        >
          <div className="grid gap-4">
            <div className="grid gap-2">
              <Label htmlFor="matrix-name">Name</Label>
              <Input
                id="matrix-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Competitive Landscape"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="matrix-template" className="gap-1.5">
                Start from
                {TEMPLATES_ENABLED ? null : <ComingSoonBadge />}
              </Label>
              <Select
                value={templateId === "" ? "blank" : templateId}
                onValueChange={handlePickTemplate}
                disabled={!TEMPLATES_ENABLED || templatesLoading}
              >
                <SelectTrigger id="matrix-template" className="w-full">
                  <SelectValue
                    placeholder={
                      templatesLoading ? "Loading templates..." : "Blank matrix"
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="blank">Blank matrix</SelectItem>
                  {templates && templates.length > 0 ? (
                    <SelectGroup>
                      <SelectLabel>Featured templates</SelectLabel>
                      {templates.map((template) => (
                        <SelectItem
                          key={template.id}
                          value={template.id}
                          className="pr-2 data-[state=checked]:pr-8 [&>span:last-child]:flex-1"
                        >
                          {template.name}
                          <BetaBadge size="sm" className="ml-auto" />
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  ) : null}
                </SelectContent>
              </Select>
              <p className="text-muted-foreground text-xs">
                {!TEMPLATES_ENABLED
                  ? "Starting from a premade matrix template is coming soon. New matrices begin blank, with one input column."
                  : templateId
                    ? "The template's sections become the matrix's columns, and every row runs it as its analysis."
                    : "A blank matrix starts with one input column; add analysis steps as columns yourself."}
              </p>
            </div>
            {error ? (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            ) : null}
          </div>
          <DialogFooter className="mt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => setOpen(false)}
              disabled={isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={!canSubmit || isPending}>
              {isPending ? "Creating..." : "Create"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
